import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, sanitizeApiErrorMessage } from "@/shared/lib/apiClient";
import { fetchAdminUsers } from "../api/adminApi";
import { getAuthToken, isSuperAdminUser } from "../auth";
import { userAccounts } from "../data";
import type { UserAccount } from "../types";

export type AdminUsersFilters = {
  role?: string;
  name?: string;
  email?: string;
};

/** Rôles API → clés d'interface ("enterprise" = Assurance, "hospital" = Sinistre). */
function mapApiRole(role: string): UserAccount["role"] {
  if (role === "insurer") return "enterprise";
  if (role === "claims") return "hospital";
  if (role === "admin" || role === "super_admin" || role === "auditor") {
    return role;
  }
  return "hospital";
}

function initialFromName(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : "?";
}

export function useAdminUsers() {
  const [users, setUsers] = useState<UserAccount[]>(userAccounts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async (filters: AdminUsersFilters = {}) => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setUsers(userAccounts);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchAdminUsers({
        role: filters.role || undefined,
        name: filters.name || undefined,
        email: filters.email || undefined,
      });
      setUsers(
        api.users.map((row) => ({
          id: String(row.id),
          name: row.name,
          email: row.email,
          role: mapApiRole(row.role),
          initial: initialFromName(row.name),
        })),
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(null);
        setUsingApi(false);
        return;
      }
      setError(
        sanitizeApiErrorMessage(
          err instanceof Error ? err.message : "Impossible de charger les utilisateurs.",
        ),
      );
      setUsers(userAccounts);
      setUsingApi(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({});
  }, [load]);

  const roleOptions = useMemo(() => {
    const options = [
      { value: "", label: "Tous les rôles" },
      { value: "insurer", label: "Assurance" },
      { value: "claims", label: "Sinistre" },
      { value: "auditor", label: "Audit" },
    ];
    if (isSuperAdminUser()) {
      options.splice(1, 0, { value: "admin", label: "Admin" }, { value: "super_admin", label: "Super admin" });
    }
    return options;
  }, []);

  return {
    users,
    loading,
    error,
    usingApi,
    roleOptions,
    reload: load,
  };
}
